import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Lock, Mail, ArrowRight, ShieldCheck, X, KeyRound, Eye, EyeOff } from "lucide-react";

function ForgotPasswordModal({ defaultEmail, onClose }) {
  const [email, setEmail] = useState(defaultEmail || "");
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSending(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setDone(true);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="forgot-password-modal"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white w-full max-w-md border-t-4 border-[#FF5A00]"
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-neutral-200">
          <div className="flex items-center gap-2">
            <KeyRound className="w-4 h-4 text-[#FF5A00]" />
            <div className="font-display font-bold text-lg">Recuperar contraseña</div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="hover:bg-neutral-100 p-2"
            data-testid="forgot-password-close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {done ? (
          <div className="p-6 space-y-4" data-testid="forgot-password-success">
            <div className="font-mono-kpi text-[10px] uppercase tracking-[0.2em] text-[#00C853]">
              // Solicitud procesada
            </div>
            <p className="text-sm text-neutral-700 leading-relaxed">
              Si el email está registrado, tu contraseña fue restablecida a la contraseña estándar
              <span className="font-mono bg-neutral-100 px-1.5 py-0.5 mx-1 border border-neutral-200">AA123!</span>.
              Inicia sesión y cámbiala desde Ajustes.
            </p>
            <button
              onClick={onClose}
              className="w-full bg-[#0A0A0A] hover:bg-[#FF5A00] text-white font-mono-kpi text-xs uppercase tracking-[0.2em] px-6 py-3 transition-colors"
              data-testid="forgot-password-done-button"
            >
              Entendido
            </button>
          </div>
        ) : (
          <form onSubmit={submit} className="p-6 space-y-4" data-testid="forgot-password-form">
            <p className="text-sm text-neutral-600 leading-relaxed">
              Ingresa tu email corporativo y restableceremos tu contraseña a la estándar
              <span className="font-mono bg-neutral-100 px-1.5 py-0.5 mx-1 border border-neutral-200">AA123!</span>.
            </p>
            <div>
              <label className="block font-mono-kpi text-[10px] uppercase tracking-[0.2em] text-neutral-500 mb-2">
                Email
              </label>
              <div className="relative">
                <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
                <input
                  data-testid="forgot-password-email-input"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="tunombre@allactive.cl"
                  className="w-full pl-10 pr-4 py-3 border border-neutral-300 focus:border-[#FF5A00] focus:ring-2 focus:ring-[#FF5A00]/15 outline-none bg-white text-sm"
                />
              </div>
            </div>
            {error && (
              <div className="border border-[#D50000]/30 bg-[#D50000]/5 text-[#B71C1C] text-sm px-4 py-3 font-medium">
                {error}
              </div>
            )}
            <button
              data-testid="forgot-password-submit"
              type="submit"
              disabled={sending}
              className="w-full bg-[#0A0A0A] hover:bg-[#FF5A00] text-white font-mono-kpi text-xs uppercase tracking-[0.2em] px-6 py-3 transition-colors disabled:opacity-50"
            >
              {sending ? "Enviando…" : "Restablecer contraseña"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showForgot, setShowForgot] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const u = await login(email, password);
      navigate(u?.role === "technician" ? "/tecnicos" : "/admin");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-[#fafafa]" data-testid="login-page">
      {/* Left form panel */}
      <div className="flex flex-col justify-between p-8 lg:p-16 bg-white border-t-4 border-[#FF5A00]">
        <div>
          <div className="flex items-center gap-3" data-testid="login-brand">
            <img src="/aa-logo.png" alt="All Active" className="h-10 w-auto" />
          </div>
        </div>

        <div className="max-w-md w-full mx-auto py-12">
          <div className="font-mono-kpi text-[11px] uppercase tracking-[0.2em] text-[#FF5A00] mb-3">
            // Acceso restringido
          </div>
          <h1 className="font-display text-4xl sm:text-5xl font-black leading-[0.95] mb-2">
            Inicia sesión<br />en el panel.
          </h1>
          <p className="text-neutral-500 text-sm mb-10 max-w-sm leading-relaxed">
            Panel interno de operaciones de All Active. Mantenciones, cotizaciones, cobros y clientes en un solo lugar.
          </p>

          <form onSubmit={submit} className="space-y-5" data-testid="login-form">
            <div>
              <label className="block font-mono-kpi text-[10px] uppercase tracking-[0.2em] text-neutral-500 mb-2">
                Email
              </label>
              <div className="relative">
                <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
                <input
                  data-testid="login-email-input"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="admin@allactive.cl"
                  className="w-full pl-10 pr-4 py-3 border border-neutral-300 focus:border-[#FF5A00] focus:ring-2 focus:ring-[#FF5A00]/15 outline-none bg-white text-sm"
                />
              </div>
            </div>

            <div>
              <label className="block font-mono-kpi text-[10px] uppercase tracking-[0.2em] text-neutral-500 mb-2">
                Contraseña
              </label>
              <div className="relative">
                <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
                <input
                  data-testid="login-password-input"
                  type={showPassword ? "text" : "password"}
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••••••"
                  className="w-full pl-10 pr-11 py-3 border border-neutral-300 focus:border-[#FF5A00] focus:ring-2 focus:ring-[#FF5A00]/15 outline-none bg-white text-sm"
                />
                <button
                  type="button"
                  data-testid="login-toggle-password"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-neutral-400 hover:text-[#FF5A00] transition-colors"
                  aria-label={showPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {error && (
              <div
                data-testid="login-error"
                className="border border-[#D50000]/30 bg-[#D50000]/5 text-[#B71C1C] text-sm px-4 py-3 font-medium"
              >
                {error}
              </div>
            )}

            <button
              data-testid="login-submit-button"
              type="submit"
              disabled={loading}
              className="group w-full bg-[#0A0A0A] hover:bg-[#FF5A00] text-white font-medium px-6 py-4 transition-colors flex items-center justify-between disabled:opacity-50"
            >
              <span className="font-mono-kpi text-xs uppercase tracking-[0.2em]">
                {loading ? "Verificando…" : "Iniciar sesión"}
              </span>
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </button>

            <div className="pt-1 text-right">
              <button
                type="button"
                onClick={() => setShowForgot(true)}
                data-testid="forgot-password-link"
                className="font-mono-kpi text-[11px] uppercase tracking-[0.18em] text-neutral-500 hover:text-[#FF5A00] transition-colors"
              >
                ¿Olvidaste tu contraseña?
              </button>
            </div>
          </form>

          <div className="mt-10 pt-6 border-t border-neutral-200 flex items-center gap-3 text-xs text-neutral-500">
            <ShieldCheck className="w-4 h-4 text-[#00C853]" />
            <span>Conexión protegida · JWT + bcrypt · Bloqueo anti-fuerza-bruta</span>
          </div>
        </div>

        <div className="font-mono-kpi text-[10px] uppercase tracking-[0.2em] text-neutral-400">
          © {new Date().getFullYear()} All Active · allactive.cl
        </div>
      </div>

      {/* Right industrial image panel */}
      <div className="hidden lg:block relative overflow-hidden bg-black">
        <img
          src="https://images.unsplash.com/photo-1722925541321-f52d45b29c17?crop=entropy&cs=srgb&fm=jpg&q=85"
          alt="Equipamiento de gimnasio"
          className="absolute inset-0 w-full h-full object-cover opacity-50"
        />
        <div className="absolute inset-0 bg-gradient-to-tr from-black via-black/70 to-transparent" />
        <div className="absolute inset-0 flex flex-col justify-end p-12 text-white">
          <div className="font-mono-kpi text-[11px] uppercase tracking-[0.25em] text-[#FF5A00] mb-4">
            // Mantención · Reparación · Optimización
          </div>
          <div className="font-display text-4xl xl:text-5xl font-black leading-tight max-w-md">
            La operación de tu gimnasio,<br />
            <span className="text-[#FF5A00]">en control total.</span>
          </div>
          <div className="mt-6 text-sm text-neutral-300 max-w-md">
            Calendario inteligente, informes con fotos, cotizaciones y cobros — todo conectado en un solo panel.
          </div>
        </div>
      </div>

      {showForgot && (
        <ForgotPasswordModal
          defaultEmail={email}
          onClose={() => setShowForgot(false)}
        />
      )}
    </div>
  );
}
